import React, { useState } from 'react';
import { BarChart, PieChart, LineChart, Calendar, Clock, Users, CheckCircle, AlertCircle, ChevronDown, Download, RefreshCw, ArrowUpRight, ArrowDownRight, Filter } from 'lucide-react';
export const WorkflowAnalytics = ({
  workflows
}) => {
  const [dateRange, setDateRange] = useState('last30Days');
  const [filterDepartment, setFilterDepartment] = useState('all');
  // Calculate metrics based on workflows data
  const totalWorkflows = workflows.length;
  const activeWorkflows = workflows.filter(w => w.status === 'In Progress').length;
  const completedWorkflows = workflows.filter(w => w.status === 'Completed').length;
  const pendingWorkflows = workflows.filter(w => w.status === 'Pending').length;
  // Average completion time (simulated)
  const avgCompletionDays = 12;
  // Workflow status distribution
  const statusDistribution = [{
    name: 'In Progress',
    value: activeWorkflows,
    color: 'bg-blue-500'
  }, {
    name: 'Completed',
    value: completedWorkflows,
    color: 'bg-green-500'
  }, {
    name: 'Pending',
    value: pendingWorkflows,
    color: 'bg-yellow-500'
  }, {
    name: 'Rejected',
    value: workflows.filter(w => w.status === 'Rejected').length,
    color: 'bg-red-500'
  }];
  // Department distribution
  const departmentDistribution = [{
    name: 'General Manager Office',
    value: 35,
    color: 'bg-purple-500'
  }, {
    name: 'Finance Department',
    value: 25,
    color: 'bg-blue-500'
  }, {
    name: 'HR Department',
    value: 20,
    color: 'bg-green-500'
  }, {
    name: 'IT Department',
    value: 15,
    color: 'bg-yellow-500'
  }, {
    name: 'Legal Department',
    value: 5,
    color: 'bg-red-500'
  }];
  // Monthly workflow trends (simulated data)
  const monthlyTrends = [{
    month: 'Jan',
    created: 12,
    completed: 8
  }, {
    month: 'Feb',
    created: 15,
    completed: 10
  }, {
    month: 'Mar',
    created: 18,
    completed: 12
  }, {
    month: 'Apr',
    created: 14,
    completed: 13
  }, {
    month: 'May',
    created: 20,
    completed: 15
  }, {
    month: 'Jun',
    created: 22,
    completed: 18
  }, {
    month: 'Jul',
    created: 25,
    completed: 20
  }, {
    month: 'Aug',
    created: 28,
    completed: 22
  }];
  // Top bottlenecks (simulated data)
  const bottlenecks = [{
    step: 'Finance Approval',
    avgDays: 5.2,
    workflows: 8
  }, {
    step: 'Legal Review',
    avgDays: 4.8,
    workflows: 6
  }, {
    step: 'GM Approval',
    avgDays: 3.5,
    workflows: 10
  }, {
    step: 'Document Review',
    avgDays: 2.3,
    workflows: 12
  }];
  // Sample data
  const workflowPerformance = [{
    name: 'Annual Budget Approval',
    department: 'Finance Department',
    status: 'In Progress',
    started: 'Aug 15, 2023',
    completionTime: 'Est. 5 days',
    participants: ['JA', 'FA', 'MA'],
    performance: 85
  }, {
    name: 'Employee Onboarding Process',
    department: 'HR Department',
    status: 'Pending',
    started: 'Aug 20, 2023',
    completionTime: 'Est. 3 days',
    participants: ['JA', 'HA'],
    performance: 70
  }, {
    name: 'Project Proposal Review',
    department: 'General Manager Office',
    status: 'Completed',
    started: 'Jul 10, 2023',
    completionTime: '4.2 days',
    participants: ['JA', 'NA', 'AK'],
    performance: 92
  }, {
    name: 'IT Equipment Procurement',
    department: 'IT Department',
    status: 'In Progress',
    started: 'Aug 18, 2023',
    completionTime: 'Est. 7 days',
    participants: ['NA', 'MA', 'JA'],
    performance: 60
  }, {
    name: 'Contract Review Process',
    department: 'Legal Department',
    status: 'Completed',
    started: 'Aug 5, 2023',
    completionTime: '6.5 days',
    participants: ['AK', 'JA'],
    performance: 78
  }, {
    name: 'Audit Planning',
    department: 'Audit Department',
    status: 'In Progress',
    started: 'Aug 22, 2023',
    completionTime: 'Est. 10 days',
    participants: ['FA', 'JA', 'AK', 'MA'],
    performance: 45
  }];
  return <div className="space-y-6" data-id="element-1335">
      <div className="flex justify-between items-center" data-id="element-1336">
        <h2 className="text-lg font-medium text-gray-800" data-id="element-1337">
          Workflow Analytics
        </h2>
        <div className="flex items-center space-x-3" data-id="element-1338">
          <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={dateRange} onChange={e => setDateRange(e.target.value)} data-id="element-1339">
            <option value="last7Days" data-id="element-1340">Last 7 Days</option>
            <option value="last30Days" data-id="element-1341">Last 30 Days</option>
            <option value="last90Days" data-id="element-1342">Last 90 Days</option>
            <option value="lastYear" data-id="element-1343">Last Year</option>
            <option value="custom" data-id="element-1344">Custom Range</option>
          </select>
          <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1345">
            <Filter className="h-4 w-4" data-id="element-1346" />
            <span data-id="element-1347">Filters</span>
            <ChevronDown className="h-4 w-4" data-id="element-1348" />
          </button>
          <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1349">
            <RefreshCw className="h-4 w-4" data-id="element-1350" />
            <span data-id="element-1351">Refresh</span>
          </button>
          <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1352">
            <Download className="h-4 w-4" data-id="element-1353" />
            <span data-id="element-1354">Export</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-6" data-id="element-1355">
        <MetricCard title="Total Workflows" value="42" change="+8%" isPositive={true} icon={<BarChart className="h-5 w-5 text-blue-600" data-id="element-1357" />} color="blue" data-id="element-1356" />
        <MetricCard title="Completed Workflows" value="18" change="+12%" isPositive={true} icon={<CheckCircle className="h-5 w-5 text-green-600" data-id="element-1359" />} color="green" data-id="element-1358" />
        <MetricCard title="Active Participants" value="87" change="+5%" isPositive={true} icon={<Users className="h-5 w-5 text-purple-600" data-id="element-1361" />} color="purple" data-id="element-1360" />
        <MetricCard title="Avg. Completion Time" value="4.3 days" change="-0.5 days" isPositive={true} icon={<Clock className="h-5 w-5 text-yellow-600" data-id="element-1363" />} color="yellow" data-id="element-1362" />
      </div>
      <div className="grid grid-cols-2 gap-6" data-id="element-1364">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1365">
          <div className="flex justify-between items-center mb-6" data-id="element-1366">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-1367">
              Workflow Status Distribution
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1368">
              View Details
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-1369">
            {/* Placeholder for chart */}
            <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-1370">
              <PieChart className="h-16 w-16 text-gray-300" data-id="element-1371" />
              <span className="ml-3 text-gray-400" data-id="element-1372">
                Status Distribution Chart
              </span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-4" data-id="element-1373">
            <StatusLegendItem status="In Progress" count="24" color="bg-blue-100 text-blue-800" data-id="element-1374" />
            <StatusLegendItem status="Completed" count="18" color="bg-green-100 text-green-800" data-id="element-1375" />
            <StatusLegendItem status="Pending" count="8" color="bg-yellow-100 text-yellow-800" data-id="element-1376" />
            <StatusLegendItem status="Rejected" count="2" color="bg-red-100 text-red-800" data-id="element-1377" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1378">
          <div className="flex justify-between items-center mb-6" data-id="element-1379">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-1380">
              Workflow Completion Trend
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1381">
              View Details
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-1382">
            {/* Placeholder for chart */}
            <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-1383">
              <LineChart className="h-16 w-16 text-gray-300" data-id="element-1384" />
              <span className="ml-3 text-gray-400" data-id="element-1385">Completion Trend Chart</span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-1386">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1387">
          <div className="flex justify-between items-center mb-6" data-id="element-1388">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-1389">
              Workflows by Department
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1390">
              View Details
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-1391">
            {/* Placeholder for chart */}
            <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-1392">
              <BarChart className="h-16 w-16 text-gray-300" data-id="element-1393" />
              <span className="ml-3 text-gray-400" data-id="element-1394">Department Chart</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1395">
          <div className="flex justify-between items-center mb-6" data-id="element-1396">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-1397">
              Workflow Types
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1398">
              View Details
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-1399">
            {/* Placeholder for chart */}
            <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-1400">
              <PieChart className="h-16 w-16 text-gray-300" data-id="element-1401" />
              <span className="ml-3 text-gray-400" data-id="element-1402">Workflow Types Chart</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1403">
          <div className="flex justify-between items-center mb-6" data-id="element-1404">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-1405">Bottlenecks</h3>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1406">
              View Details
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-1407">
            {/* Placeholder for chart */}
            <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-1408">
              <BarChart className="h-16 w-16 text-gray-300" data-id="element-1409" />
              <span className="ml-3 text-gray-400" data-id="element-1410">Bottlenecks Chart</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1411">
        <div className="flex justify-between items-center mb-6" data-id="element-1412">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-1413">
            Workflow Performance
          </h3>
          <div className="flex items-center space-x-3" data-id="element-1414">
            <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700" data-id="element-1415">
              <Filter className="h-4 w-4" data-id="element-1416" />
              <span data-id="element-1417">Filter</span>
              <ChevronDown className="h-4 w-4" data-id="element-1418" />
            </button>
            <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700" data-id="element-1419">
              <Download className="h-4 w-4" data-id="element-1420" />
              <span data-id="element-1421">Export</span>
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1422">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1423">
            <thead className="bg-gray-50" data-id="element-1424">
              <tr data-id="element-1425">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1426">
                  Workflow Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1427">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1428">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1429">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1430">
                  Completion Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1431">
                  Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1432">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1433">
              {workflowPerformance.map((workflow, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-1434">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1435">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1436">
                      {workflow.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1437">
                    {workflow.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1438">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusStyles(workflow.status)}`} data-id="element-1439">
                      {workflow.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1440">
                    {workflow.started}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1441">
                    {workflow.completionTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1442">
                    <div className="flex -space-x-2" data-id="element-1443">
                      {workflow.participants.map((participant, i) => <div key={i} className="h-6 w-6 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-xs font-medium text-blue-800" title={participant} data-id="element-1444">
                          {participant.substring(0, 2)}
                        </div>)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1445">
                    <div className="flex items-center" data-id="element-1446">
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1447">
                        <div className={`h-2.5 rounded-full ${getPerformanceColor(workflow.performance)}`} style={{
                      width: `${workflow.performance}%`
                    }} data-id="element-1448"></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-500" data-id="element-1449">
                        {workflow.performance}%
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
// Helper components
const MetricCard = ({
  title,
  value,
  change,
  isPositive,
  icon,
  color
}) => {
  const getColorClass = color => {
    switch (color) {
      case 'blue':
        return 'bg-blue-50';
      case 'green':
        return 'bg-green-50';
      case 'purple':
        return 'bg-purple-50';
      case 'yellow':
        return 'bg-yellow-50';
      default:
        return 'bg-gray-50';
    }
  };
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1450">
      <div className="flex justify-between items-start" data-id="element-1451">
        <div data-id="element-1452">
          <p className="text-sm text-gray-500" data-id="element-1453">{title}</p>
          <h3 className="text-2xl font-bold text-gray-800 mt-1" data-id="element-1454">{value}</h3>
          <div className="flex items-center mt-2" data-id="element-1455">
            {isPositive ? <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" data-id="element-1456" /> : <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" data-id="element-1457" />}
            <span className={`text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1458">
              {change} from last period
            </span>
          </div>
        </div>
        <div className={`h-12 w-12 rounded-lg ${getColorClass(color)} flex items-center justify-center`} data-id="element-1459">
          {icon}
        </div>
      </div>
    </div>;
};
const StatusLegendItem = ({
  status,
  count,
  color
}) => {
  return <div className="flex flex-col items-center" data-id="element-1460">
      <span className={`px-2 py-1 rounded-full text-xs ${color}`} data-id="element-1461">
        {status}
      </span>
      <span className="mt-1 text-sm font-medium text-gray-800" data-id="element-1462">{count}</span>
    </div>;
};
// Helper functions
const getStatusStyles = status => {
  switch (status) {
    case 'In Progress':
      return 'bg-blue-100 text-blue-800';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
const getPerformanceColor = performance => {
  if (performance >= 80) return 'bg-green-600';
  if (performance >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};
const getImpactColor = days => {
  if (days >= 5) return 'bg-red-500';
  if (days >= 3) return 'bg-yellow-500';
  return 'bg-green-500';
};
const getImpactLevel = days => {
  if (days >= 5) return 'High';
  if (days >= 3) return 'Medium';
  return 'Low';
};
const getCompletionRateColor = rate => {
  const percentage = parseInt(rate);
  if (percentage >= 90) return 'bg-green-500';
  if (percentage >= 75) return 'bg-yellow-500';
  return 'bg-red-500';
};
const PieSegment = ({
  startAngle,
  endAngle,
  color
}) => {
  // Calculate SVG path for pie segment
  const radius = 40;
  const cx = 50;
  const cy = 50;
  // Convert angles to radians
  const startRad = (startAngle - 90) * Math.PI / 180;
  const endRad = (endAngle - 90) * Math.PI / 180;
  // Calculate coordinates
  const x1 = cx + radius * Math.cos(startRad);
  const y1 = cy + radius * Math.sin(startRad);
  const x2 = cx + radius * Math.cos(endRad);
  const y2 = cy + radius * Math.sin(endRad);
  // Determine if the arc should be drawn as a large arc
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
  // Create the SVG path
  const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  return <path d={path} fill={color} data-id="element-1463" />;
};